import * as React from "react"
import { FC, useState } from "react"
import { Icon } from "semantic-ui-react"
import he from "he"
import { navigate } from "gatsby"
import styled from "styled-components"
import { black, white, otherBorderGrey } from "../../constants/colors"
import { desktop } from "../../constants/screenSizes"
import { cleanUrl, lockScroll, safeHeDecode } from "../../util/generalUtil"
import { MenuItemType } from "../../model/MenuItemType"

const MobileContainer = styled.div`
  @media (min-width: ${desktop}px) {
    display: none;
  }
`

const BurgerContainer = styled.div`
  padding: 10px;
  cursor: pointer;
`

const InnerContainer = styled.div`
  position: relative;
  pointer-events: none;
`

const BackgroundContainer = styled.div`
  background: ${black};
  opacity: 0;
  position: absolute;
  height: 100%;
  width: 100%;
  pointer-events: none;
  transition: all 0.2s ease-in-out;
  cursor: pointer;

  > div {
    width: calc(100% + 300px);
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
  }
`

const Container = styled.div<{ isLogoAbsent: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 50;
  transition: all 0.2s;
  pointer-events: none;

  ${({ isActive }) =>
    isActive
      ? `
      ${InnerContainer} {
        right: 0;
        pointer-events: auto;
      }
      
      ${BackgroundContainer} {
        opacity: 0.9;
        pointer-events: auto;
      }
    `
      : ""}
`

const ItemsContainer = styled.div<{ isOpen: boolean }>`
  width: 100%;
  max-width: 500px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  background-color: ${white};
  left: -100vw;
  transition: all 0.2s ease-in-out;

  ${({ isOpen }) => (isOpen ? "left: 0;" : "")}
`

const LinksContainer = styled.div`
  height: calc(100vh - 70px);
  overflow: auto;
`

const CloseContainer = styled.div`
  padding: 20px;
  display: flex;
  justify-content: space-between;

  i {
    margin: 0;
  }
`

const Right = styled.div`
  margin-left: auto;
`

const ItemContainer = styled.div`
  width: 100%;
  border-bottom: 1px solid ${otherBorderGrey};

  a {
    width: 100%;
    height: 100%;
    padding: 20px;
    display: block;
  }
`

const StyledButton = styled.button`
  width: 100%;
  height: 100%;
  padding: 20px;
  border: none;
  background: none;
  text-align: left;
  display: flex;
  justify-content: space-between;

  &:focus {
    border: none !important;
    outline: none;
  }
`

interface Props {
  menuItems: MenuItemType[]
}
const MobileMenu: FC<Props> = ({ menuItems }) => {
  const [isActive, setIsActive] = useState(false)
  const [menuStack, setMenuStack] = useState([])

  const addMenu = (item: string) => () => {
    setMenuStack([...menuStack, item])
  }

  const handleNavigate = (to: string) => {
    lockScroll(false)

    setIsActive(false)
    setMenuStack([])
    navigate(to)
  }

  const handleToggle = () => {
    if (menuStack.length === 0) {
      document.documentElement.style.overflowY = "hidden"
      setIsActive(true)
      setMenuStack(["-"])
    } else {
      document.documentElement.style.overflowY = "scroll"
      setIsActive(false)
      setMenuStack([])
    }
  }

  const handleBack = () => {
    setMenuStack([...menuStack.slice(0, menuStack.length - 1)])
  }

  const renderMenu = (
    menu: MenuItemType[],
    menuKey?: string,
    allUrl?: string,
    allLabel?: string
  ) => {
    if (!menu || menu.length < 1) {
      return null
    }

    return (
      <>
        <ItemsContainer isOpen={menuStack.includes(menuKey)}>
          <CloseContainer>
            {menuKey !== "-" && (
              <div>
                <Icon name="arrow left" size="large" onClick={handleBack} />
              </div>
            )}
            <Right>
              <Icon name="close" size="large" onClick={handleToggle} />
            </Right>
          </CloseContainer>
          <LinksContainer>
            {menu.map((item: MenuItemType, index: number) => {
              const { label, url, children } = item

              return (
                <ItemContainer key={`${url}${index}`}>
                  {children.length > 0 ? (
                    <StyledButton onClick={addMenu(`${menuKey}${index}`)}>
                      {safeHeDecode(label)} <Icon name="angle right" />
                    </StyledButton>
                  ) : (
                    <StyledButton onClick={() => handleNavigate(cleanUrl(url))}>
                      {safeHeDecode(label)}
                    </StyledButton>
                  )}
                </ItemContainer>
              )
            })}
            {allUrl && allUrl !== "#" && (
              <ItemContainer key={allUrl}>
                <StyledButton onClick={() => handleNavigate(cleanUrl(allUrl))}>
                  <b>All {he.decode(allLabel)}</b>
                </StyledButton>
              </ItemContainer>
            )}
          </LinksContainer>
        </ItemsContainer>
        {menu.map((item: MenuItemType, index: number) =>
          renderMenu(item.children, `${menuKey}${index}`, item.url, item.label)
        )}
      </>
    )
  }

  return (
    <MobileContainer>
      <BurgerContainer onClick={handleToggle}>
        <Icon name="bars" size="large" />
      </BurgerContainer>
      <Container isActive={isActive}>
        <BackgroundContainer onClick={handleToggle}>
          <div>Click to continue shopping</div>
        </BackgroundContainer>
        <InnerContainer>{renderMenu(menuItems, "-")}</InnerContainer>
      </Container>
    </MobileContainer>
  )
}

export default MobileMenu
